import { FC, useEffect, useState, useMemo } from 'react';
import HelperService from '../../services/helper'
import useNotyf from '../../context/useNotify';
import { League } from '../../models/league';
import Dropdown from '../common/Dropdown';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsersCog, faCalendarAlt, faUserCog, faChartBar } from "@fortawesome/free-solid-svg-icons";
import { QueryParams } from '../../models/misc';
import { StyledInstructions } from './Styles';
import LeagueService from "../../services/league"
import FixtureTable from './Leagues/FixtureTable';
import TeamTable from './Leagues/TeamTable';
import UserTable from './Leagues/UserTable';
import instructions from "../../content-options/admin/instructions"
import { useUser } from '../../context/useUser';
import AdminLeagueService from '../../services/admin/adminLeague';
import ReactModal from '../common/ReactModal';
import ConfirmationModalContent from '../common/ConfirmationModalContent';

const LeaguesTables: FC = () => {
  const notyf = useNotyf()
  const {user} = useUser()

  // Data
    const [leagues, setLeagues] = useState<League[]>([]);
    const [league, setLeague] = useState<League>();

  
  const leagueOptions = useMemo(() => {
    return leagues.map(league => ({ 
      label: `${league.leagueName} - DIV ${league.division}`, 
      value: league 
    }));
  }, [leagues]);
    
  // API
  const fetchLeagues = async () => {
    try {
      const filters: QueryParams = { 
        page: 1,
        pageSize: 100,
      };
      const data = await LeagueService.listLeagues(filters)
      setLeagues(data.data)
      return data.data
    } catch (err) {
      console.log("Error fetching leagues: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching leagues.")
    }
  }
    
  const fetchLeague = async (id: string) => {
    try {
      const league = await LeagueService.getLeagueById(id)
        setLeague(league)
    } catch (err) {
      console.log("Error fetching league: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching league.")
    }
  }

  const updateLeagueStats = async (id: string) => {
    try {
      await AdminLeagueService.updateLeagueStats(id);
      notyf.success("League stats updated.")
    } catch (err) {
      console.log("Error updating league stats: ", err);
      HelperService.showNotfyErr(notyf, err, "Error updating league stats.");
    }
  }

  // Methods
  const handleSelectLeague = (selectedOption: any) => {
      if (!selectedOption) {
          return
      }
    const selectedLeagueId = selectedOption.value._id;
    fetchLeague(selectedLeagueId)
    setTab('Fixture')
  };
    
    const refreshLeague = () => {
        if (!league) {
            return
        }
        fetchLeague(league?._id)
    }

    const [ tab, setTab ] = useState('Fixture')
    const handleChangeView = (view: string) => {
        setTab(view)
    }

  // Hooks
  useEffect(() => {
    const init = async () => {
      const data = await fetchLeagues()
      if (data?.length) {
        fetchLeague(data[0]._id)
      }
    }
    init()
  }, []);

    // Modals
    const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);

    const closeModals = () => {
        setShowStatsModal(false)
        setSelectedLeagueId(null);
    };

    // Modal - Stats
    const [showStatsModal, setShowStatsModal] = useState<boolean>(false);

    const handleClickStatsModal = () => {
      if (!league) {
        return
      }
      setSelectedLeagueId(league._id);
      setShowStatsModal(true);
    }

    const handleClickUpdateLeagueStats = async () => {
      if (selectedLeagueId) {
        await updateLeagueStats(selectedLeagueId);
      }
      closeModals();
      fetchLeagues()
      setSelectedLeagueId(null);
    }

  return (
    <>
      <div className='d-flex flex-column'>

        {/* Instructions */}
        <StyledInstructions className='mb-2'>
          { instructions.table.leagueManagement }
        </StyledInstructions>

        {/* League Details */}
        <StyledInstructions>
          <div className='d-flex gap-3 justify-content-end flex-wrap'>
          <div>
              Fixtures: <strong className="text-info">{league?.fixtures.length || 0}</strong>
          </div>
          <div>
              Teams: <strong className="text-info">{league?.teams.length || 0}</strong>
          </div>
          <div>
              Start Date: <strong className="text-info">{league?.startDate ? league.startDate : "Not Selected"}</strong>
          </div>
          <div> 
            Division: <strong className="text-warning">{league?.division ?? "Not Selected"}</strong>
          </div>
          <div> 
            League: <strong className="text-warning">{league?.leagueName ?? "Not Selected"}</strong>
          </div>
          </div>
        </StyledInstructions>
        
        {/* Actions */}
        <div className='d-flex justify-content-end gap-3 my-2 flex-wrap'>
          <button className={`btn btn-warning`} onClick={handleClickStatsModal}
            >
            <FontAwesomeIcon icon={faChartBar} /> Calculate Stats
          </button>
          <button className={`btn ${tab === 'User' ? 'btn-primary' : 'btn-dark'}`} onClick={() => handleChangeView('User')}
          >
            <FontAwesomeIcon icon={faUserCog} /> Manage Users
          </button>
          <button className={`btn ${tab === 'Team' ? 'btn-primary' : 'btn-dark'}`} onClick={() => handleChangeView('Team')}
          >
            <FontAwesomeIcon icon={faUsersCog} /> Manage Teams
          </button>
          <button className={`btn ${tab === 'Fixture' ? 'btn-primary' : 'btn-dark'}`} onClick={() => handleChangeView('Fixture')}>
            <FontAwesomeIcon icon={faCalendarAlt} /> Manage Fixtures
          </button>
          <Dropdown
              options={leagueOptions}
              onSelect={handleSelectLeague}
              text="Select League"
          />
        </div>

        <hr className="divider p-0 m-0 mb-2"></hr>

        {/* User table */}
        {league && tab === 'User' ?
        <UserTable 
            league={league} 
            refresh={refreshLeague} 
        />
        : null}
        {/* Team table */}
        {league && tab === 'Team' ?
        <TeamTable 
            league={league} 
            fetchLeague={refreshLeague} 
        />
        : null}
        {/* Fixtures table */}
        {league && tab === 'Fixture'?
        <FixtureTable 
            league={league} 
            fetchLeague={refreshLeague} 
        />
        : null}
 
      </div>

      {/* Stats Modal */}
      <ReactModal
        modalOpen={showStatsModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickUpdateLeagueStats}
          onCancel={() => closeModals()}
          message="Are you sure you want to recalculate the league stats?  This action will update the team hub stats for this league."
        />}
        closeModal={() => closeModals()}
      />
    </>
  );
};

export default LeaguesTables;

